<!--
  Client Portal v2: a single 3-band STACKED bar (PR D).

  Renders ONE banded bucket (budget / allowance / external cents + total) as
  a horizontal stacked bar, each band sized by its share of the bucket total
  and colored from the canonical BAND_DEFS. Replaces the v1 single-color
  bars. Zero-value bands are omitted (no zero-width segment).

  Empty / not-applicable state (build contract PR D inline design bit b): a
  bucket whose total is 0 -- e.g. a store that is card-pay with no budget or
  allowance configured, so it has nothing to show in those bands -- renders a
  muted "not applicable" track + label rather than an empty/blank bar. The
  caller passes ``emptyLabel`` to tailor the message per surface.
-->
<template>
  <div class="band-bar" :data-testid="testid">
    <template v-if="hasData">
      <div class="band-bar__track" role="img" :aria-label="ariaLabel">
        <div
          v-for="seg in segments"
          :key="seg.key"
          class="band-bar__segment"
          :data-testid="`band-segment-${seg.key}`"
          :style="{ width: seg.pct + '%', backgroundColor: seg.color }"
          :title="`${seg.label}: ${seg.pctLabel}%`"
        />
      </div>
    </template>
    <div
      v-else
      class="band-bar__empty"
      data-testid="band-bar-empty"
    >
      <span class="band-bar__empty-track" />
      <span class="band-bar__empty-label">{{ emptyLabel }}</span>
    </div>
  </div>
</template>

<script>
import { BAND_DEFS } from "./bandColors";

export default {
  name: "BandStackedBar",
  props: {
    // { budget_cents, allowance_cents, external_cents, total_cents }
    bucket: {
      type: Object,
      required: true,
    },
    testid: {
      type: String,
      default: "band-stacked-bar",
    },
    emptyLabel: {
      type: String,
      default: "Card-pay store -- no budget or allowance configured",
    },
  },
  computed: {
    total() {
      const b = this.bucket || {};
      // Prefer the server-provided total; fall back to the band sum so a
      // bucket without an explicit total_cents still renders correctly.
      if (typeof b.total_cents === "number" && b.total_cents > 0) {
        return b.total_cents;
      }
      return (
        (b.budget_cents || 0) +
        (b.allowance_cents || 0) +
        (b.external_cents || 0)
      );
    },
    hasData() {
      return this.total > 0;
    },
    segments() {
      if (!this.hasData) return [];
      const b = this.bucket || {};
      return BAND_DEFS.map((def) => {
        const cents = b[def.centsKey] || 0;
        const pct = (cents / this.total) * 100;
        return {
          key: def.key,
          label: def.label,
          color: def.color,
          cents,
          pct,
          // Rounded for the title/test assertions; the style uses the raw
          // pct so segments fill the track exactly.
          pctLabel: pct.toFixed(1),
        };
      }).filter((seg) => seg.cents > 0);
    },
    ariaLabel() {
      return this.segments
        .map((s) => `${s.label} ${s.pctLabel}%`)
        .join(", ");
    },
  },
};
</script>

<style scoped>
.band-bar {
  width: 100%;
}
.band-bar__track {
  display: flex;
  width: 100%;
  height: 14px;
  border-radius: var(--tf-radius-pill, 9999px);
  overflow: hidden;
  background: var(--tf-surface-dark, #e8eff5);
}
.band-bar__segment {
  height: 100%;
}
.band-bar__segment:first-child {
  border-top-left-radius: var(--tf-radius-pill, 9999px);
  border-bottom-left-radius: var(--tf-radius-pill, 9999px);
}
.band-bar__segment:last-child {
  border-top-right-radius: var(--tf-radius-pill, 9999px);
  border-bottom-right-radius: var(--tf-radius-pill, 9999px);
}
.band-bar__empty {
  display: flex;
  align-items: center;
  gap: var(--tf-sp-3, 12px);
}
.band-bar__empty-track {
  display: inline-block;
  flex: 0 0 64px;
  height: 14px;
  border-radius: var(--tf-radius-pill, 9999px);
  background: repeating-linear-gradient(
    45deg,
    var(--tf-surface-dark, #e8eff5),
    var(--tf-surface-dark, #e8eff5) 4px,
    var(--tf-surface-alt, #f5f5f5) 4px,
    var(--tf-surface-alt, #f5f5f5) 8px
  );
}
.band-bar__empty-label {
  font-size: 0.8125rem;
  color: var(--tf-fg-4, #757575);
  font-style: italic;
}
</style>
